/**
 * §H1 compare — "level select (0/1/2) → searchable unit checklist (scoped by country
 * filter; enforce 2–4 with toast) → indicator select (★-prefixed contractual first,
 * default current base)".
 *
 * Two departures from the mockup, both because the list is now the real hierarchy:
 *
 * * **the checklist is filtered *and* capped.** 1 048 communes cannot be a list, and they
 *   cannot be 1 048 checkboxes in a 170px scroll box either: level 2 would mount a
 *   thousand inputs in one render for a form that accepts four. So `MAX_LISTED_UNITS`
 *   rows are drawn and a line says how many matched, which is the prompt to type. The
 *   picked units stay picked while the filter narrows — a reader who ticks Bale, types
 *   "kad" and ticks Kadiogo has picked two, not one — and a pick that falls outside the
 *   cap is still listed, because a checkbox you cannot see is one you cannot untick.
 * * **`operational: false`.** The §F5.8 cascade lists only units with reported MELS
 *   activity, because that is what its map can paint. An analysis is a different question:
 *   §E4 aggregates over any unit, and `spi` (M4 Task 6) is a *derived* series that no
 *   operational flag counts, so filtering here would hide units that do have an answer.
 *
 * The result panels are §H1's: the LineChart in the CMP colours the **server** assigned
 * (`series[].color`), the ★ contractual table with its unit columns headed in those same
 * colours, a client-built CSV, and "Highlight on map", which registers the payload's own
 * `result_layers[0]` — so the outline on the map and the line in the chart are one palette
 * read twice rather than two palettes hoped to agree.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  MAX_COMPARE_UNITS,
  MIN_COMPARE_UNITS,
  useToolRun,
  type CompareResult,
} from '../../../lib/analysis';
import { fetchIndicators, fetchUnitOptions, queryKeys } from '../../../lib/api';
import { LineChart } from '../../../components/charts';
import { fmtNum } from '../../../lib/format';
import { Icon } from '../../../lib/icons';
import { selectScopeParent, useAppStore, type ReportingLevel } from '../../../lib/state';
import { toast } from '../../../lib/toast';
import type { ToolProps } from '../AnalysisDock';
import { ToolFrame } from '../ToolFrame';
import { ResultBox, ResultNote } from '../ResultBox';
import { downloadCsv, toCsv } from '../csv';

const LEVELS: ReportingLevel[] = [0, 1, 2];

/**
 * How many checkboxes the list draws at once — see the module header. Big enough that the
 * three countries and the ~50 regions are never truncated, small enough that the communes
 * are.
 */
export const MAX_LISTED_UNITS = 60;

/** The §F1 level labels the rest of the app uses, so the dock names a level as the map does. */
const LEVEL_KEY: Record<number, string> = {
  0: 'indicators.levelCountry',
  1: 'indicators.levelRegion',
  2: 'indicators.levelCommune',
};

export function Compare({ onBack }: ToolProps) {
  const { t } = useTranslation();
  const country = useAppStore((s) => s.country);
  const parent = useAppStore((s) => selectScopeParent(s));
  const baseIndicator = useAppStore((s) => s.indicator);
  const addResultLayer = useAppStore((s) => s.addResultLayer);

  const [level, setLevel] = useState<number>(1);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [indicatorId, setIndicatorId] = useState<string>(baseIndicator ?? 'b21');
  const run = useToolRun<CompareResult>('compare');

  const { data: units } = useQuery({
    queryKey: queryKeys.unitOptions('admin', level, country, parent, false),
    queryFn: () => fetchUnitOptions('admin', level, country, parent, false),
  });
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });

  /** §H1: "★-prefixed contractual first". Catalogue order is preserved inside each group. */
  const indicatorOptions = useMemo(() => {
    const rows = indicators ?? [];
    return [...rows.filter((i) => i.tier === 'contractual'), ...rows.filter((i) => i.tier !== 'contractual')];
  }, [indicators]);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (units ?? []).filter((u) => !needle || u.name.toLowerCase().includes(needle));
  }, [units, query]);

  /** The capped list, plus any pick the cap would have hidden (module header). */
  const visible = useMemo(() => {
    if (matches.length <= MAX_LISTED_UNITS) return matches;
    const shown = matches.slice(0, MAX_LISTED_UNITS);
    return [...shown, ...matches.slice(MAX_LISTED_UNITS).filter((u) => picked.includes(u.id))];
  }, [matches, picked]);

  const toggle = (id: string) => {
    setPicked((current) => {
      if (current.includes(id)) return current.filter((x) => x !== id);
      if (current.length >= MAX_COMPARE_UNITS) {
        toast(t('analysis.compare.max', { max: MAX_COMPARE_UNITS }));
        return current;
      }
      return [...current, id];
    });
  };

  /** Changing the level changes what a unit id *means*, so the picks go with it. */
  const changeLevel = (next: number) => {
    setLevel(next);
    setPicked([]);
  };

  const submit = () => {
    if (picked.length < MIN_COMPARE_UNITS) {
      toast(t('analysis.compare.min', { min: MIN_COMPARE_UNITS }));
      return;
    }
    run.run({ unit_ids: picked, indicator: indicatorId });
  };

  const result = run.result;

  const highlight = () => {
    const spec = result?.result_layers?.[0];
    if (!spec) return;
    addResultLayer(spec, {
      jobId: run.jobId ?? spec.id,
      tool: 'compare',
      label: t('analysis.compare.layerLabel', { indicator: result?.indicator.name ?? '' }),
    });
    toast(t('analysis.compare.highlighted'));
  };

  const exportCsv = () => {
    if (!result) return;
    const header = [t('analysis.csv.indicator'), t('analysis.csv.unit'), ...result.years.map(String)];
    const rows = result.series.map((s) => [result.indicator.name, s.name, ...s.values]);
    downloadCsv('orgunit_comparison.csv', toCsv(header, rows));
  };

  return (
    <ToolFrame
      tool="compare"
      onBack={onBack}
      phase={run.phase}
      progress={run.progress}
      error={run.error}
      cached={run.cached}
      result={
        result ? (
          <>
            <ResultBox title={t('analysis.compare.trend', { indicator: result.indicator.name })} pad>
              <LineChart
                labels={result.years}
                unit={result.indicator.unit}
                fmt={result.indicator.fmt}
                series={result.series.map((s) => ({
                  name: s.name,
                  short: s.name.slice(0, 9),
                  color: s.color,
                  values: s.values,
                }))}
              />
            </ResultBox>

            <ResultBox
              title={t('analysis.compare.contractual', { year: result.year })}
              scroll
              className="cmp-table"
            >
              <table className="mini">
                <thead>
                  <tr>
                    <th>{t('analysis.csv.indicator')}</th>
                    {result.series.map((s) => (
                      <th key={s.unit_id} className="num" style={{ color: s.color, textAlign: 'right' }}>
                        {s.name.slice(0, 10)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.contractual_table.map((row) => (
                    <tr key={row.indicator_id}>
                      <td>{row.name.split(' ').slice(0, 3).join(' ')}</td>
                      {result.series.map((s) => (
                        <td key={s.unit_id} className="num">
                          {fmtNum(row.values[s.unit_id] ?? null, row.fmt)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ResultBox>

            <div className="ad-actions">
              <button type="button" className="btn primary sm" id="cmpMap" onClick={highlight}>
                <Icon name="map" size={12} /> {t('analysis.compare.highlight')}
              </button>
              <button type="button" className="btn ghost sm" id="cmpCsv" onClick={exportCsv}>
                <Icon name="download" size={12} /> {t('analysis.csv.button')}
              </button>
            </div>
            {result.mode === 'cumulative' ? (
              <ResultNote>{t('analysis.compare.cumulative')}</ResultNote>
            ) : null}
          </>
        ) : null
      }
    >
      <label className="fld" htmlFor="cmpLv">
        {t('analysis.compare.level')}
      </label>
      <select
        className="std"
        id="cmpLv"
        value={level}
        onChange={(e) => changeLevel(Number(e.target.value))}
      >
        {LEVELS.map((lv) => (
          <option key={String(lv)} value={String(lv)}>
            {t(LEVEL_KEY[Number(lv)] ?? 'indicators.levelRegion')}
          </option>
        ))}
      </select>

      <label className="fld" htmlFor="cmpSearch">
        {t('analysis.compare.units')}{' '}
        <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>
          {t('analysis.compare.pick', { min: MIN_COMPARE_UNITS, max: MAX_COMPARE_UNITS })}
        </span>
      </label>
      <input
        className="std"
        id="cmpSearch"
        placeholder={t('analysis.compare.filter')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 6 }}
      />
      <div className="ad-list" id="cmpList">
        {visible.map((u) => (
          <label key={u.id}>
            <input type="checkbox" value={u.id} checked={picked.includes(u.id)} onChange={() => toggle(u.id)} />
            {u.name}
            <small>{u.country ?? ''}</small>
          </label>
        ))}
        {visible.length === 0 ? <div className="ad-status">{t('analysis.compare.noUnits')}</div> : null}
      </div>
      {matches.length > MAX_LISTED_UNITS ? (
        <div className="ad-status" data-note="narrow">
          {t('analysis.compare.narrow', { shown: MAX_LISTED_UNITS, total: matches.length })}
        </div>
      ) : null}

      <label className="fld" htmlFor="cmpInd">
        {t('analysis.compare.indicator')}
      </label>
      <select
        className="std"
        id="cmpInd"
        value={indicatorId}
        onChange={(e) => setIndicatorId(e.target.value)}
      >
        {indicatorOptions.map((i) => (
          <option key={i.id} value={i.id}>
            {i.tier === 'contractual' ? '★ ' : ''}
            {i.name}
          </option>
        ))}
      </select>

      <button
        type="button"
        className="btn primary ad-run"
        id="cmpRun"
        disabled={run.phase === 'running'}
        onClick={submit}
      >
        <Icon name="play" size={13} /> {t('analysis.compare.run')}
      </button>
    </ToolFrame>
  );
}

export default Compare;
